/**
 * Business Profile Onboarding Page
 *
 * First-time setup for users to configure their business profile.
 * Required before accessing manual entry features.
 */

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useSetupBusinessProfile } from '@/hooks/business/useBusinessProfile';
import {
  BusinessProfileSetupRequest,
  FormatType,
  BAHAMAS_ISLANDS,
} from '@/types/business';

interface OnboardingFormData {
  employer_ni_number: string;
  company_name: string;
  format_type: FormatType;
  trading_name?: string;
  industry_sector?: string;
  contact_email?: string;
  contact_phone?: string;
  island?: string;
}

const INDUSTRY_SECTORS = [
  'Hospitality & Tourism',
  'Financial Services',
  'Retail & Wholesale',
  'Construction',
  'Healthcare',
  'Education',
  'Transportation',
  'Manufacturing',
  'Professional Services',
  'Other',
];

export default function Onboarding() {
  const navigate = useNavigate();
  const setupMutation = useSetupBusinessProfile();
  const [step, setStep] = useState(1);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<OnboardingFormData>({
    defaultValues: {
      format_type: 'NON_HOSPITALITY',
    },
  });

  // React Hook Form's watch() returns new instances, cannot be memoized
  const formatType = watch('format_type');

  const onSubmit = async (data: OnboardingFormData) => {
    try {
      const request: BusinessProfileSetupRequest = {
        employer_ni_number: data.employer_ni_number,
        company_name: data.company_name,
        format_type: data.format_type,
        trading_name: data.trading_name || undefined,
        industry_sector: data.industry_sector || undefined,
        contact_email: data.contact_email || undefined,
        contact_phone: data.contact_phone || undefined,
        island: data.island || undefined,
      };

      await setupMutation.mutateAsync(request);
      toast.success('Business profile created successfully!');
      navigate('/nib-c10/manual-entry');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Setup failed';
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Set Up Your Business
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Complete your business profile to start using NIB manual entry
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  step >= 1
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                1
              </div>
              <div
                className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}
              />
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  step >= 2
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                2
              </div>
            </div>
            <div className="flex justify-center mt-2 text-xs text-gray-500">
              <span className="w-24 text-center">Business Info</span>
              <span className="w-24 text-center">Contact Details</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {step === 1 && (
              <>
                {/* Employer NI Number */}
                <div>
                  <label
                    htmlFor="employer_ni_number"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Employer NI Number *
                  </label>
                  <input
                    type="text"
                    id="employer_ni_number"
                    {...register('employer_ni_number', {
                      required: 'Employer NI number is required',
                      pattern: {
                        value: /^\d{8}$/,
                        message: 'Must be exactly 8 digits',
                      },
                    })}
                    placeholder="12345678"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.employer_ni_number && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.employer_ni_number.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Your NIB employer registration number
                  </p>
                </div>

                {/* Company Name */}
                <div>
                  <label
                    htmlFor="company_name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    {...register('company_name', {
                      required: 'Company name is required',
                      minLength: {
                        value: 2,
                        message: 'Must be at least 2 characters',
                      },
                    })}
                    autoComplete="organization"
                    placeholder="Acme Corporation Ltd"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.company_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.company_name.message}
                    </p>
                  )}
                </div>

                {/* Trading Name */}
                <div>
                  <label
                    htmlFor="trading_name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Trading Name
                  </label>
                  <input
                    type="text"
                    id="trading_name"
                    {...register('trading_name')}
                    placeholder="Acme Co (optional)"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If different from company name
                  </p>
                </div>

                {/* Format Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Type *
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="NON_HOSPITALITY"
                        {...register('format_type', { required: true })}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          Non-Hospitality
                        </span>
                        <span className="block text-xs text-gray-500">
                          Standard C10 format (80-byte records)
                        </span>
                      </div>
                    </label>
                    <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        value="HOSPITALITY"
                        {...register('format_type', { required: true })}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">
                          Hospitality
                        </span>
                        <span className="block text-xs text-gray-500">
                          Includes gratuity/tips fields (115-byte records)
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {formatType === 'HOSPITALITY' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg
                        className="h-5 w-5 text-blue-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="ml-3 text-sm text-blue-700">
                        Hospitality businesses must report employee
                        gratuities/tips separately for NIB contribution
                        calculations.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={async () => {
                      const valid = await trigger(['employer_ni_number', 'company_name']);
                      if (valid) setStep(2);
                    }}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Next
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                {/* Industry Sector */}
                <div>
                  <label
                    htmlFor="industry_sector"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Industry Sector
                  </label>
                  <select
                    id="industry_sector"
                    {...register('industry_sector')}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select an industry...</option>
                    {INDUSTRY_SECTORS.map(sector => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Island */}
                <div>
                  <label
                    htmlFor="island"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Island
                  </label>
                  <select
                    id="island"
                    {...register('island')}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select an island...</option>
                    {BAHAMAS_ISLANDS.map(island => (
                      <option key={island} value={island}>
                        {island}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contact Email */}
                <div>
                  <label
                    htmlFor="contact_email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Contact Email
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    {...register('contact_email', {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Invalid email address',
                      },
                    })}
                    autoComplete="email"
                    placeholder="payroll@company.com"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.contact_email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.contact_email.message}
                    </p>
                  )}
                </div>

                {/* Contact Phone */}
                <div>
                  <label
                    htmlFor="contact_phone"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="contact_phone"
                    {...register('contact_phone')}
                    autoComplete="tel"
                    placeholder="(242) 555-1234"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={setupMutation.isPending}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                  >
                    {setupMutation.isPending ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Setting up...
                      </>
                    ) : (
                      'Complete Setup'
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        <p className="mt-4 text-center text-sm text-gray-500">
          You can update these details later in Settings
        </p>
      </div>
    </div>
  );
}
